/**
 * JsonTree — Reusable structured JSON viewer for plugin Renderers.
 *
 * Features:
 * - Recursive collapsible tree with type-coloured values (string/number/boolean/null).
 * - Right-click any node to copy its value, JSON, minified JSON, or accessor path.
 * - Long string truncation with click-to-expand.
 * - Array preview limit for very large arrays.
 * - Configurable default expand depth.
 *
 * Use this in any plugin Renderer that needs to display structured data
 * (HTTP response, SQL rows, MCP tool output, formula result, file content, etc.).
 *
 * @example
 *   <JsonTree value={output.data} />
 *   <JsonTree value={response.body} defaultExpandDepth={2} />
 */

import { useEffect, useState, type ReactNode } from "react";
import { usePluginSdkT } from "../i18n";

/* ── Type helpers ── */

export type JsonKind =
    | "object"
    | "array"
    | "string"
    | "number"
    | "boolean"
    | "null";

export function jsonKindOf(value: unknown): JsonKind {
    if (value === null || value === undefined) return "null";
    if (Array.isArray(value)) return "array";
    const t = typeof value;
    if (t === "string" || t === "number" || t === "boolean") return t;
    return "object";
}

const TONE_BY_KIND: Record<JsonKind, string> = {
    object: "text-foreground",
    array: "text-foreground",
    string: "plugin-tone-success-text",
    number: "plugin-tone-info-text",
    boolean: "plugin-tone-warning-text",
    null: "plugin-tone-muted-text italic",
};

/** Compact human-readable summary for a JSON value. */
export function summarizeJson(value: unknown): { kind: JsonKind; label: string } {
    const kind = jsonKindOf(value);
    if (kind === "object") {
        const n = Object.keys(value as Record<string, unknown>).length;
        return { kind, label: `object · ${n} ${n === 1 ? "key" : "keys"}` };
    }
    if (kind === "array") {
        const n = (value as unknown[]).length;
        return { kind, label: `array · ${n} ${n === 1 ? "item" : "items"}` };
    }
    if (kind === "string") {
        const len = (value as string).length;
        return { kind, label: `string · ${len} chars` };
    }
    return { kind, label: kind };
}

function safeStringify(value: unknown): string {
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
}

/** Build a JS-style accessor path: `$.users[0].name`. */
function formatPath(segments: ReadonlyArray<string | number>): string {
    if (segments.length === 0) return "$";
    let out = "$";
    for (const s of segments) {
        if (typeof s === "number") out += `[${s}]`;
        else if (/^[A-Za-z_$][\w$]*$/.test(s)) out += `.${s}`;
        else out += `[${JSON.stringify(s)}]`;
    }
    return out;
}

async function copyToClipboard(text: string): Promise<void> {
    try {
        await navigator.clipboard.writeText(text);
    } catch {
        /* clipboard unavailable */
    }
}

/* ── Context menu ── */

interface MenuState {
    x: number;
    y: number;
    value: unknown;
    path: Array<string | number>;
}

function ContextMenu({
    state,
    onClose,
}: {
    state: MenuState;
    onClose: () => void;
}) {
    const t = usePluginSdkT();
    useEffect(() => {
        const close = () => onClose();
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("click", close);
        window.addEventListener("contextmenu", close);
        window.addEventListener("keydown", onKey);
        window.addEventListener("scroll", close, true);
        return () => {
            window.removeEventListener("click", close);
            window.removeEventListener("contextmenu", close);
            window.removeEventListener("keydown", onKey);
            window.removeEventListener("scroll", close, true);
        };
    }, [onClose]);

    const kind = jsonKindOf(state.value);
    const isPrimitive = kind !== "object" && kind !== "array";
    const valueText = isPrimitive
        ? (kind === "string" ? (state.value as string) : String(state.value))
        : safeStringify(state.value);
    const jsonText = safeStringify(state.value);
    const pathText = formatPath(state.path);

    const items: Array<{ label: string; hint?: string; run: () => void }> = [];
    if (isPrimitive) {
        items.push({
            label: t("jsonTree.copyValue"),
            hint: kind,
            run: () => copyToClipboard(valueText),
        });
        items.push({
            label: t("jsonTree.copyAsJson"),
            hint: "with quotes",
            run: () => copyToClipboard(jsonText),
        });
    } else {
        items.push({
            label: t("jsonTree.copyAsJson"),
            hint: kind,
            run: () => copyToClipboard(jsonText),
        });
        items.push({
            label: t("jsonTree.copyMinifiedJson"),
            hint: "minified",
            run: () => copyToClipboard(JSON.stringify(state.value)),
        });
    }
    items.push({
        label: t("jsonTree.copyPath"),
        hint: pathText.length > 32 ? `${pathText.slice(0, 32)}…` : pathText,
        run: () => copyToClipboard(pathText),
    });

    const menuWidth = 220;
    const menuHeight = items.length * 28 + 32;
    const left = Math.min(state.x, Math.max(8, window.innerWidth - menuWidth - 8));
    const top = Math.min(state.y, Math.max(8, window.innerHeight - menuHeight - 8));

    return (
        <div
            className="fixed z-50 min-w-[200px] rounded-md border border-border bg-popover shadow-lg py-1 text-[11px]"
            style={{ left, top }}
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
        >
            <div className="px-2 py-1 plugin-tone-muted-text font-mono truncate border-b border-border/60 mb-1">
                {pathText}
            </div>
            {items.map((it, i) => (
                <button
                    key={i}
                    type="button"
                    className="w-full flex items-center justify-between gap-3 px-2 py-1 hover:bg-muted text-left"
                    onClick={() => {
                        it.run();
                        onClose();
                    }}
                >
                    <span className="text-foreground">{it.label}</span>
                    {it.hint ? (
                        <span className="plugin-tone-muted-text text-[10px] font-mono truncate max-w-[110px]">
                            {it.hint}
                        </span>
                    ) : null}
                </button>
            ))}
        </div>
    );
}

/* ── Tree node ── */

const STRING_TRUNCATE = 120;

interface JsonNodeProps {
    value: unknown;
    label?: string;
    isIndex?: boolean;
    trailingComma?: boolean;
    depth: number;
    path: Array<string | number>;
    defaultExpandDepth: number;
    arrayPreviewLimit: number;
    onContext: (state: MenuState) => void;
}

function JsonStringLeaf({ value, tone }: { value: string; tone: string }) {
    const long = value.length > STRING_TRUNCATE;
    const [expanded, setExpanded] = useState(false);
    const text = !long || expanded ? value : `${value.slice(0, STRING_TRUNCATE)}…`;
    return (
        <span className={tone}>
            "{text}"
            {long ? (
                <button
                    type="button"
                    className="ml-1 text-[10px] plugin-tone-muted-text hover:underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        setExpanded((x) => !x);
                    }}
                >
                    [{expanded ? "collapse" : `+${value.length - STRING_TRUNCATE} chars`}]
                </button>
            ) : null}
        </span>
    );
}

function JsonLeaf({ value }: { value: unknown }) {
    const kind = jsonKindOf(value);
    const tone = TONE_BY_KIND[kind];

    if (kind === "string") {
        return <JsonStringLeaf value={value as string} tone={tone} />;
    }
    if (kind === "null") return <span className={tone}>null</span>;
    return <span className={tone}>{String(value)}</span>;
}

function JsonNode({
    value,
    label,
    isIndex,
    trailingComma,
    depth,
    path,
    defaultExpandDepth,
    arrayPreviewLimit,
    onContext,
}: JsonNodeProps) {
    const kind = jsonKindOf(value);
    const isContainer = kind === "object" || kind === "array";
    const [expanded, setExpanded] = useState(depth < defaultExpandDepth);

    const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onContext({ x: e.clientX, y: e.clientY, value, path });
    };

    const labelEl: ReactNode = label !== undefined
        ? (
            <span>
                {isIndex ? (
                    <span className="plugin-tone-muted-text">{label}</span>
                ) : (
                    <span className="text-foreground/85 font-medium">"{label}"</span>
                )}
                <span className="plugin-tone-muted-text">: </span>
            </span>
        )
        : null;

    if (!isContainer) {
        return (
            <div
                className="leading-relaxed rounded px-0.5 -mx-0.5 hover:bg-muted/30"
                onContextMenu={handleContextMenu}
            >
                {labelEl}
                <JsonLeaf value={value} />
                {trailingComma ? <span className="plugin-tone-muted-text">,</span> : null}
            </div>
        );
    }

    const open = kind === "object" ? "{" : "[";
    const close = kind === "object" ? "}" : "]";
    const entries = kind === "object"
        ? (value && typeof value === "object")
            ? Object.entries(value as Record<string, unknown>)
            : []
        : (value as unknown[]).map((v, i) => [String(i), v] as [string, unknown]);
    const empty = entries.length === 0;
    const overflow = kind === "array" && entries.length > arrayPreviewLimit;
    const visible = overflow ? entries.slice(0, arrayPreviewLimit) : entries;

    if (empty) {
        return (
            <div
                className="leading-relaxed rounded px-0.5 -mx-0.5 hover:bg-muted/30"
                onContextMenu={handleContextMenu}
            >
                {labelEl}
                <span className="plugin-tone-muted-text">{open}{close}</span>
                {trailingComma ? <span className="plugin-tone-muted-text">,</span> : null}
            </div>
        );
    }

    return (
        <div className="leading-relaxed">
            <button
                type="button"
                className="text-left rounded px-0.5 -mx-0.5 hover:bg-muted/40 transition-colors w-full"
                onClick={() => setExpanded((x) => !x)}
                onContextMenu={handleContextMenu}
            >
                <span className="plugin-tone-muted-text mr-1 select-none">
                    {expanded ? "▾" : "▸"}
                </span>
                {labelEl}
                <span className="plugin-tone-muted-text">{open}</span>
                {!expanded ? (
                    <span className="plugin-tone-muted-text ml-1">
                        {kind === "object"
                            ? `${entries.length} ${entries.length === 1 ? "key" : "keys"}`
                            : `${entries.length} ${entries.length === 1 ? "item" : "items"}`}
                        {" "}{close}{trailingComma ? "," : ""}
                    </span>
                ) : null}
            </button>

            {expanded ? (
                <>
                    <div className="pl-4 border-l border-border/60 ml-1">
                        {visible.map(([k, v], i) => {
                            const childIsIndex = kind === "array";
                            const childPath: Array<string | number> = [
                                ...path,
                                childIsIndex ? Number(k) : k,
                            ];
                            return (
                                <JsonNode
                                    key={`${depth}-${k}-${i}`}
                                    value={v}
                                    label={k}
                                    isIndex={childIsIndex}
                                    trailingComma={i < visible.length - 1 || overflow}
                                    depth={depth + 1}
                                    path={childPath}
                                    defaultExpandDepth={defaultExpandDepth}
                                    arrayPreviewLimit={arrayPreviewLimit}
                                    onContext={onContext}
                                />
                            );
                        })}
                        {overflow ? (
                            <div className="plugin-tone-muted-text italic">
                                … {entries.length - arrayPreviewLimit} more items
                            </div>
                        ) : null}
                    </div>
                    <div
                        className="plugin-tone-muted-text rounded px-0.5 -mx-0.5 hover:bg-muted/30"
                        onContextMenu={handleContextMenu}
                    >
                        {close}
                        {trailingComma ? "," : ""}
                    </div>
                </>
            ) : null}
        </div>
    );
}

/* ── Public component ── */

export interface JsonTreeProps {
    /** Any JSON-serialisable value. */
    value: unknown;
    /** Default depth at which nodes are expanded (0 = collapse top, 1 = expand top, …). */
    defaultExpandDepth?: number;
    /** Max number of array items rendered before "+ N more". */
    arrayPreviewLimit?: number;
    /** Extra class for the outer wrapper. */
    className?: string;
}

/**
 * Recursive JSON viewer with right-click "copy value / JSON / path" menu.
 *
 * Drop into any plugin Renderer body. Wrap in `CollapsibleBlock` for the
 * standard plugin block chrome.
 */
export function JsonTree({
    value,
    defaultExpandDepth = 1,
    arrayPreviewLimit = 100,
    className,
}: JsonTreeProps) {
    const [menu, setMenu] = useState<MenuState | null>(null);

    return (
        <>
            <div className={`select-text font-mono text-[12px] ${className ?? ""}`.trim()}>
                <JsonNode
                    value={value}
                    depth={0}
                    path={[]}
                    defaultExpandDepth={defaultExpandDepth}
                    arrayPreviewLimit={arrayPreviewLimit}
                    onContext={setMenu}
                />
            </div>
            {menu ? (
                <ContextMenu state={menu} onClose={() => setMenu(null)} />
            ) : null}
        </>
    );
}
